/**
 * FilevineEntityDetails - Expanded details view for a Filevine collection item
 *
 * Orchestrates the display of provider cards and field grids when a row is expanded.
 * Uses extracted components for clean separation of concerns.
 */

import React from 'react';

import { Card, CardContent } from '@/components/ui/card';
import { CustomFieldType } from '@/services/api/entitySyncService';
import type {
  DetailedFilevineCollectionItemSchema,
  FilevineSectionFieldConfigSchema,
  FilevineSectionPayload,
  ProjectClientSchema,
} from '@/services/api/generated/entitySyncService';

import type { FilevineSectionFieldValue } from '../utils/fieldEditors';
import { getSuggestedValue, isFieldAffected } from '../utils/fieldMatching';
import {
  getDisplayableFields,
  getFieldValue,
  sortFieldsByPosition,
} from '../utils/fieldUtils';
import { isPersonValue } from '../utils/typeGuards';
import { ContactCard, fromProjectClientSchema } from './ContactCard';
import { LabeledField } from './LabeledField';

// Re-export for consumers
export type { FilevineSectionFieldValue } from '../utils/fieldEditors';

// ============================================================================
// Types
// ============================================================================

export interface FilevineEntityDetailsProps {
  /** The Filevine item to display. Optional for CREATE scenarios. */
  item?: DetailedFilevineCollectionItemSchema;
  /** Field configurations for this section */
  fieldConfigs: FilevineSectionFieldConfigSchema[];
  /** Set of field internal names that have suggested changes */
  affectedFields?: Set<string>;
  /** Payload containing field updates to apply */
  suggestedPayload?: FilevineSectionPayload;
  /** Whether fields should be editable */
  editable?: boolean;
  /** Callback when a field value changes (fieldInternalName, newValue) */
  onPayloadChange?: (
    fieldName: string,
    value: FilevineSectionFieldValue,
  ) => void;
  /** Firm ID for resolving linked contacts */
  firmId?: string;
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Provider Card - Prominent display of person/provider information
 * Wraps ContactCard with a styled container.
 */
const ProviderCardWrapper: React.FC<{
  person: NonNullable<ReturnType<typeof getFieldValue>>;
  label: string;
}> = ({ person, label }) => {
  // Type guard already verified this in parent
  if (!isPersonValue(person)) return null;

  return (
    <Card className="border-zinc-200 bg-white">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100">
            <span className="text-lg font-semibold text-emerald-600">
              {(person as ProjectClientSchema).fullname.charAt(0).toUpperCase()}
            </span>
          </div>

          {/* Contact details */}
          <div className="min-w-0 flex-1">
            <ContactCard
              contact={fromProjectClientSchema(person as ProjectClientSchema)}
              label={label}
              variant="inline"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Field Grid - Two-column grid layout for fields
 */
const FieldGrid: React.FC<{
  fields: FilevineSectionFieldConfigSchema[];
  item?: DetailedFilevineCollectionItemSchema;
  affectedFields?: Set<string>;
  suggestedPayload?: FilevineSectionPayload;
  editable?: boolean;
  isCreate: boolean;
  onPayloadChange?: (
    fieldName: string,
    value: FilevineSectionFieldValue,
  ) => void;
  firmId?: string;
}> = ({
  fields,
  item,
  affectedFields,
  suggestedPayload,
  editable,
  isCreate,
  onPayloadChange,
  firmId,
}) => {
  // Group fields into rows of 2
  const rowCount = Math.ceil(fields.length / 2);

  return (
    <div className={editable ? 'divide-y divide-zinc-100' : undefined}>
      {Array.from({ length: rowCount }, (_, rowIndex) => {
        const rowFields = fields.slice(rowIndex * 2, rowIndex * 2 + 2);
        return (
          <div
            key={rowIndex}
            className="grid grid-cols-2 gap-x-8 py-4 first:pt-0 last:pb-0"
          >
            {rowFields.map((fc) => {
              const affectedSet = affectedFields ?? new Set<string>();
              const hasChange = isFieldAffected(fc, affectedSet);
              // In edit mode, always get suggested value; otherwise only if hasChange
              const suggestedValue =
                editable || hasChange
                  ? getSuggestedValue(fc, suggestedPayload)
                  : undefined;

              return (
                <LabeledField
                  key={fc.id}
                  label={fc.name}
                  value={getFieldValue(item, fc)}
                  fieldType={fc.custom_field_type}
                  fieldConfig={fc}
                  isRequired={fc.is_required}
                  hasChange={hasChange}
                  suggestedValue={suggestedValue}
                  isCreate={isCreate}
                  editable={editable}
                  onValueChange={
                    onPayloadChange
                      ? (value) => onPayloadChange(fc.internal_name, value)
                      : undefined
                  }
                  firmId={firmId}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const FilevineEntityDetails: React.FC<FilevineEntityDetailsProps> = ({
  item,
  fieldConfigs,
  affectedFields,
  suggestedPayload,
  editable,
  onPayloadChange,
  firmId,
}) => {
  // Determine if this is a CREATE scenario (no item)
  const isCreate = !item;

  // Get all displayable fields (exclude doc gen buttons)
  const displayableFields = getDisplayableFields(fieldConfigs);

  // Find the first Person field to show as provider card
  const personField = displayableFields.find(
    (fc) => fc.custom_field_type === CustomFieldType.PERSON,
  );
  const personValue = personField ? getFieldValue(item, personField) : null;
  const hasProviderCard = personField && isPersonValue(personValue);

  // Get all fields for the grid, sorted by position
  const gridFields = sortFieldsByPosition(displayableFields);

  return (
    <div className="space-y-6">
      {/* Provider Card - prominent display at top (only when we have an item) */}
      {hasProviderCard && personField && (
        <ProviderCardWrapper person={personValue} label={personField.name} />
      )}

      {/* Two-column grid for all fields */}
      {gridFields.length > 0 && (
        <FieldGrid
          fields={gridFields}
          item={item}
          affectedFields={affectedFields}
          suggestedPayload={suggestedPayload}
          editable={editable}
          isCreate={isCreate}
          onPayloadChange={onPayloadChange}
          firmId={firmId}
        />
      )}
    </div>
  );
};

export default FilevineEntityDetails;
